<template>
  <h1>Partial Garments Report</h1>

  <div class="content-section filters">

    <div class="alert alert-warning" role="alert">After setting filters or changing sorting order, click "Apply filters"
      to re-query the database</div>
    <div class="row">
      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="Start Date"
            v-model="selectedStartDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

      <div class="col-md-4">
        <FormKit
            type="datepicker"
            label="End Date"
            v-model="selectedEndDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>
      <div class="col-md-4">
        <FormKit
            v-model="selectedPaymentStatus"
            type="radio"
            label="Payment Status"
            :options="{
              paid: 'Paid',
              unpaid: 'Unpaid',
              both: 'Both'
            }"
            outer-class="inline-input"
        />
      </div>
    </div>

    <div class="action-bar">
      <button @click="clearFilter" id="clearFilter">Clear filter</button>&#160;
      <button @click="exportZip" id="exportZip">Export Partials</button>&#160;
      <button @click="applyFilter" id="applyFilter1">Apply filter</button>
    </div>

    <div v-if="isListing" class="report-partials">

      <easy-data-table
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :headers="headers"
          :items="ordersPartials"
          :search-field="searchField"
          :search-value="searchValue"
          :loading="loading"
          border-cell
          buttons-pagination
          :prevent-context-menu-row="false"
          :key="dataTableKey"
      >
        <template #header="header">
          <div class="customize-header">

            {{ header.text }}<br />

            <input v-if="header.value !== 'operations'" v-show="header.value !== 'payStatus'" type="text" :id="'filter-' + header.value" @click.stop />
            <button v-else @click="applyFilter" id="applyFilter2">Apply filter</button>
          </div>
        </template>

        <template #item-orderid="item">
          <router-link :to="{ name: 'ViewOrder', params: { id: item.orderid } }">
            <div>{{ item.orderid }}</div>
          </router-link>
        </template>

        <template #item-datedue="item">
          {{formatDateEN(item.datedue)}}
        </template>

        <template #item-customer_name="item">
          <router-link :to="{ name: 'ViewCustomer', params: { id: item.customer_id } }">
            <div> {{item.firstname}} {{item.surname}}</div>
          </router-link>
        </template>

        <template #item-customer_id="item">
          <router-link :to="{ name: 'ViewCustomer', params: { id: item.customer_id } }">
            <div>{{ item.customer_id }}</div>
          </router-link>
        </template>

        <template #item-priority="item">
          <FormKit
              v-model="selectedOrderPriority[`${item.orderid}`]"
              type="autocomplete"
              name="priority"
              placeholder="Priority"
              :options="priorityList"
              :value="item.priority"
              :id="`priority_order_${item.orderid}`"
              outer-class="float-left"
              :input-class="getOrderPriorityClass(item)"
              open-on-click
              clear-search-on-open
              searchable
              selection-removable
              @input="setSelectedOrderPriority(item);"
          />
        </template>

        <template #item-user="item">
          <div style="margin-top: 5px;">
            <FormKit
                v-model="selectedOrderUser[`${item.orderid}`]"
                type="autocomplete"
                name="userid"
                placeholder="Users"
                :options="usersList"
                :value="item.username"
                :id="`order_${item.orderid}`"
                outer-class="float-left"
                open-on-click
                clear-search-on-open
                searchable
                selection-removable
                @input="setSelectedOrderUser(item);"
            />
          </div>
        </template>

        <template #item-no_of_items_left="item">
          <div class="operation-wrapper"  @click="showItemStatus(item)"><a class="selected">{{ item.numPartials }}</a></div>
          <div v-if="showItemDetails && showItemDetails.orderid === item.orderid" class="assign-user-div">
            <button type="button" class="btn-close btn-small float-right"  @click="cancelShowItems(item)" aria-label="Close"></button>
            <div class="order-detail-list">
              <table>
                <thead>
                <tr>
                  <th>Garment</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="orderItem in orderItems" style="border-bottom: solid 1px;">
                  <td>{{orderItem.garment_type.garmenttype}}</td>
                  <td v-if="orderItem.cached_status">{{serviceTypes[orderItem.cached_status.itemstatustype_id]}}</td>
                  <td v-if="!orderItem.cached_status">n/a</td>
                </tr>
                </tbody>
              </table>
            </div>


          </div>
        </template>
      </easy-data-table>
    </div>
  </div>

</template>


<script setup>
import {ref, watch} from "vue";
import OrderService from "@/services/OrderService";
import UserService from "@/services/UserService";

const getOrderPriorityClass = (item ) => {
  if (item.priority == "Low") return 'low-input';
  if (item.priority == "Medium") return 'medium-input';
  if (item.priority == "High") return 'high-input';
  return '';
};

const dataTableKey = ref(0);
const ordersPartials =  ref([]);
const searchField = ref("datedue");
const searchValue = ref("");

const usersList = ref([]);
const serverUsersOptions = ref({
  userOperator: ">=",
  userLevel: 15
});
const selectedOrder = ref("");
const selectedOrderUser = ref([]);
const selectedOrderPriority = ref([]);
const selectedItem = ref([]);
const priorityList = ref(["Low","Medium","High"]);

const nowDate = new Date();
const currentDate = new Date(Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()));
const defaultStartDate = new Date(new Date().setDate(currentDate.getDate() - 90));
const defaultEndDate = new Date(new Date().setDate(currentDate.getDate() - 1));

const selectedStartDate = ref(defaultStartDate);
const selectedEndDate = ref(defaultEndDate);
const selectedPaymentStatus = ref("paid");
const datepickerClearedKey = ref(0);

const showItemDetails = ref(null);
const orderItems = ref(null);
const serviceTypes = ref(null);

const forceRenderDataTable = () => {
  dataTableKey.value += 1;
};
const setSelectedOrderUser = (item) =>  {
  selectedOrder.value = item.orderid;
  selectedOrderUser.value[item.orderid] = item.userid;
};
const setSelectedOrderPriority = (item) =>  {
  selectedOrder.value = item.orderid;
  selectedOrderPriority.value[item.orderid] = item.priority;
  selectedItem.value = item;
};

const confirmAssignUser = (userid,orderid ) => {

  userid = (typeof userid == "undefined" )? 0 : userid;
  OrderService.patchOrderUser(orderid, userid)
      .then(response => {
        //console.log(response.data.data);
      })
      .catch(e => {
        console.log(e.response);
      });
};
const confirmAssignPriority = (priority,orderid ) => {

  priority = (typeof priority == "undefined" )? "" : priority;
  OrderService.patchOrderPriority(orderid, priority)
      .then(response => {
        //console.log(response.data.data);
        let selectedItem = {
          priority: priority
        };
        let priorityClass = getOrderPriorityClass(selectedItem );
        //id="priority_order_684210"
        let updatedRow = document.getElementById('priority_order_' + orderid);
        //remove all classes and add the new ones
        updatedRow.className = '';
        updatedRow.classList.add('formkit-input');
        updatedRow.classList.add(priorityClass);
      })
      .catch(e => {
        console.log(e.response);
      });
};

const showItemStatus = (item) => {
  OrderService.getOrderItems(item.orderid)
      .then(response => {
        orderItems.value = response.data.data.items;
        serviceTypes.value = response.data.data.service_types;
        //console.log(response.data.data);
        showItemDetails.value = item;
      })
      .catch(e => {
        console.log(e.response);
      });

};
const cancelShowItems = () => {
  showItemDetails.value = null;
};

const forceRenderDatepicker = () => {
  datepickerClearedKey.value += 1;
};
const headers = [
  { text: "Order ID", value: "orderid", sortable: true  },
  { text: "Customer Name", value: "customer_name", sortable: true  },
  { text: "Customer ID", value: "customer_id", sortable: true  },
  { text: "Number of items left", value: "no_of_items_left", sortable: true  },
  { text: "Date Due", value: "datedue", sortable: true  },
  { text: "Priority", value: "priority", sortable: true  },
  { text: "Assign to", value: "user", sortable: true  },
  { text: "Payment", value: "payStatus", sortable: true  },
  { text: "Actions", value: "operations" }
];

const isListing = ref(true);

const loading = ref(false);
const serverItemsLength = ref(0);
const serverOptions = ref({
  page: 1,
  rowsPerPage: 25,
  sortBy: ["datedue"],
  sortType: ["desc"],
  exportFormat: "none",
  start_date: defaultStartDate,
  end_date: defaultEndDate
});

const formatDateEN = (date) => {
  //25/12/2023
  const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
  var formatDate = new Date(date).toLocaleDateString('en-GB', options);
  return formatDate;
}

const  createDateAsUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
}
const loadFromServer = async () => {
  loading.value = true;
  const {
    serverCurrentPageItems,
    serverTotalItemsLength,
  } = await OrderService.searchOrdersPartials(serverOptions.value);
  ordersPartials.value = serverCurrentPageItems;
  serverItemsLength.value = serverTotalItemsLength;
  forceRenderDataTable();
  loading.value = false;
};

const applyFilter = (resetPage) => {
  let filters = {};
  let updateOptions = serverOptions.value;
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = document.getElementById("filter-" + filterProperty).value;
    }
  }
  updateOptions.filters = filters;

  if (selectedStartDate.value !== "" && typeof selectedStartDate.value != "undefined") {
    selectedStartDate.value = createDateAsUTC(new Date(selectedStartDate.value));
    updateOptions.start_date = selectedStartDate.value;
  }
  if (selectedEndDate.value !== "" && typeof selectedEndDate.value != "undefined") {
    selectedEndDate.value = createDateAsUTC(new Date(selectedEndDate.value));
    updateOptions.end_date = selectedEndDate.value;
  }
  if (selectedPaymentStatus.value !== "" && typeof selectedPaymentStatus.value != "undefined") {
    updateOptions.payment = selectedPaymentStatus.value;
  }
  
  if (resetPage) {
    updateOptions.page = 1;
  }
  serverOptions.value = updateOptions;
  loadFromServer();
};

const exportZip = async () => {
  loading.value = true;
  await OrderService.downloadOrdersPartials(serverOptions.value);
  loading.value = false;
};

const clearFilter = () => {
  let filters = {};
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = "";
      document.getElementById("filter-" + filterProperty).value = "";
    }
  }

  const updateServerOptions = serverOptions.value;
  updateServerOptions.filters = filters;
  selectedStartDate.value = "";
  selectedEndDate.value = "";
  serverOptions.value = updateServerOptions;

  forceRenderDatepicker();
  loadFromServer();
};

// initial load
loadFromServer();

watch(() => serverOptions.value.page, () => {
  applyFilter(false)
});
watch(
    () => [serverOptions.value.sortType, serverOptions.value.sortBy],
    () => {
      applyFilter(false)
    }
);
watch(() => selectedOrderUser, () => {
  confirmAssignUser(selectedOrderUser.value[selectedOrder.value],selectedOrder.value );
},{ deep: true });

watch(() => selectedOrderPriority, () => {
  confirmAssignPriority(selectedOrderPriority.value[selectedOrder.value],selectedOrder.value);

},{ deep: true });

// Get all available service type for autocomplete
UserService.getUsers(serverUsersOptions.value)
    .then(response => {
      const susers = response.data.data.users;

      usersList.value = susers.map((response) => {
        return {
          label: response.username + ' ('+response.userlevel+')',
          value: response.userid,
        }
      })
    })
    .catch(e => {
      console.log(e);
    });

</script>

<style>

.formkit-input.low-input  {
  background-color: #87CEEB!important;
}
.formkit-input.medium-input  {
  background-color: #FFFFE0!important;
}
.formkit-input.high-input  {
  background-color: #F08080!important;
}
.inline-input fieldset  {
  border: none!important;
}
.inline-input ul li {
  float: left!important;
  padding-right: 10px!important;
}
.formkit-listbox {
  overflow: auto;
  max-height: 150px;
  border: solid 1px;
  padding: 10px;
}
</style>
<style scoped>

.report-partials table,
.report-partials tbody,
.report-partials tr,
.report-partials td {
  position: relative;
  z-index: 1;
}
.assign-user-div {
  position: absolute;
  border: solid 1px;
  padding: 10px;
  width: 90%;
  background: #fff;
  z-index: 10;
}
</style>